import React from 'react';
import { Check, ChevronRight, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useRespondToCollaboration } from '../hooks/useCollaborationQueries';
import MemberAvatars from './MemberAvatars';

const groups = [
  ['pending', 'Invitations'],
  ['active', 'Active'],
  ['declined', 'Declined'],
  ['archived', 'Archived'],
];

const STATUS_LABELS = {
  pending: 'Invitation pending',
  active: 'Active collaboration',
  declined: 'Invitation declined',
  archived: 'Archived',
};

function CollaborationCard({ collaboration, group, onOpen }) {
  const respond = useRespondToCollaboration();
  const decide = async (decision) => {
    try {
      await respond.mutateAsync({ collaborationId: collaboration.id, decision });
      toast.success(decision === 'accept' ? 'Invitation accepted.' : 'Invitation declined.');
    } catch (error) {
      toast.error(error?.message || 'Unable to update this invitation.');
    }
  };

  return (
    <article className={`collab-card${group === 'active' || group === 'archived' ? ' collab-card--clickable' : ''}`}>
      <div className="collab-card__body">
        <span className={`collab-status collab-status--${group}`}>{STATUS_LABELS[group] || group}</span>
        <h3>{collaboration.patient?.name || 'Patient'}</h3>
        <p>{collaboration.reason}</p>
        <small>Invited by {collaboration.inviter?.name || 'a doctor'}</small>
      </div>
      {group === 'pending' ? (
        <div className="collab-card__actions">
          <button className="collab-button collab-button--primary" type="button" onClick={() => decide('accept')} disabled={respond.isPending}><Check size={16} /> Accept</button>
          <button className="collab-button collab-button--ghost" type="button" onClick={() => decide('decline')} disabled={respond.isPending}><X size={16} /> Decline</button>
        </div>
      ) : group === 'active' || group === 'archived' ? (
        <>
          <div className="collab-card__participants">
            {group === 'active'
              ? <MemberAvatars members={collaboration.members || []} limit={4} showStatusSummary />
              : <span className="collab-card__history">View history</span>}
            <ChevronRight className="collab-card__chevron" size={20} aria-hidden="true" />
          </div>
          <button className="collab-card__open" type="button" onClick={() => onOpen(collaboration.id)} aria-label={`Open collaboration for ${collaboration.patient?.name}`} />
        </>
      ) : null}
    </article>
  );
}

export function CollaborationLists({ data, activeGroup, onGroupChange, onOpen }) {
  return (
    <section className="collab-lists">
      <div className="collab-tabs" role="tablist" aria-label="Collaboration status">
        {groups.map(([key, label]) => (
          <button key={key} role="tab" aria-selected={activeGroup === key} className={activeGroup === key ? 'is-active' : ''} onClick={() => onGroupChange(key)}>
            {label}<span>{data?.[key]?.length || 0}</span>
          </button>
        ))}
      </div>
      <div role="tabpanel" className="collab-card-list">
        {(data?.[activeGroup] || []).map((collaboration) => <CollaborationCard key={collaboration.id} collaboration={collaboration} group={activeGroup} onOpen={onOpen} />)}
        {(data?.[activeGroup] || []).length === 0 && <div className="collab-state">No {activeGroup} collaborations.</div>}
      </div>
    </section>
  );
}

export default CollaborationLists;
